import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  AlarmReadingType,
  WebSocketsContextTypes,
  WebSocketsProviderTypes,
  ReportMessageType,
  ASSET_STATE_CHANGED_EVENT,
  ASSET_MODE_CHANGED_EVENT,
} from './types';
import { ACCESS_ROLES } from 'src/config';
import { useAuthentication } from 'src/features/authentication/context';
import { ACTIVE_ALARM_CHANGE, NEW_READINGS } from 'src/variables';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { ReadingEventMessage } from 'src/types/readings/readings';
import { setLiveDataMessages } from 'src/utilities/setLiveDataMessages';

const WebSocketsContext = React.createContext<WebSocketsContextTypes>({
  messages: [],
  alarmMessage: null,
  reportMessage: [],
  reportNotificationIds: new Set(),
  addReportNotificationId: () => {},
  assetStateMessages: [],
  assetModeMessages: [],
});

export const useWebSockets = () => useContext(WebSocketsContext);

export const WebSocketsProvider = ({ children }: WebSocketsProviderTypes) => {
  const [searchParams] = useSearchParams();

  const customerId = searchParams.get('customerId');
  const siteId = searchParams.get('siteId');

  const { user } = useAuthentication();
  const location = useLocation();

  const [messages, setMessages] = useState<ReadingEventMessage[] | ReadingEventMessage>([]);
  const [alarmMessage, setAlarmMessage] = useState<AlarmReadingType | null>(null);
  const [assetStateMessages, setAssetStateMessages] = useState<any>([]);
  const [assetModeMessages, setAssetModeMessages] = useState<any>([]);
  const [reportMessage, setReportMessage] = useState<ReportMessageType | []>([]);
  const [reportNotificationIds, setReportNotificationIds] = useState<Set<string>>(new Set());

  const addReportNotificationId = (value: string) => {
    setReportNotificationIds((prev) => new Set(prev).add(value));
  };

  const isSiteDashboardPage = location.pathname.includes('/sites-management/dashboard');

  useEffect(() => {
    if (!customerId || !user) return;

    const user_id = user.accessRole === ACCESS_ROLES.SUPER_ADMIN ? user.email : user.userId;
    const wsUrl = `${
      import.meta.env.VITE_WEBSOCKET_STREAMETRIC
    }?customerId=${customerId}&userId=${user_id}`;

    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const parsedData: ReadingEventMessage | AlarmReadingType | ReportMessageType = JSON.parse(
          event.data
        );

        if (parsedData.messageType === NEW_READINGS) {
          const readingData = parsedData as ReadingEventMessage;
          const isMessageForCurrentSite = readingData.data.siteId === siteId;

          if (siteId && isSiteDashboardPage) {
            if (isMessageForCurrentSite) {
              setMessages((prev) => {
                const prevMessage = Array.isArray(prev) ? null : prev;
                if (!prevMessage || prevMessage.data.sourceId !== readingData.data.sourceId) {
                  return readingData;
                }
                const prevItems = prevMessage.data.data;
                const newItems = readingData.data.data;
                const newItemIds = new Set(newItems.map((item) => item.id));
                const mergedData = [
                  ...prevItems.filter((item) => !newItemIds.has(item.id)),
                  ...newItems,
                ];
                return {
                  ...readingData,
                  data: {
                    ...readingData.data,
                    data: mergedData,
                  },
                };
              });
            } else {
              return;
            }
          } else {
            setMessages((prev) => {
              const prevArray = Array.isArray(prev) ? prev : prev ? [prev] : [];
              return setLiveDataMessages(prevArray, readingData);
            });
          }
        }
        if (ACTIVE_ALARM_CHANGE.includes(parsedData.messageType)) {
          setAlarmMessage(parsedData as AlarmReadingType);
        }
        if (
          parsedData.messageType.includes('REPORT_GENERATION') ||
          parsedData.messageType.includes('AI_SUMMARY')
        ) {
          setReportMessage(parsedData as ReportMessageType);
        }
        if (parsedData.messageType.includes(ASSET_STATE_CHANGED_EVENT)) {
          setAssetStateMessages((prevState: any[]) => {
            const assetStateData = parsedData as any;
            const filtered = prevState.filter(
              (item: any) => item?.data?.assetId !== assetStateData?.data?.assetId
            );
            return [...filtered, assetStateData];
          });
        }
        if (parsedData.messageType === ASSET_MODE_CHANGED_EVENT) {
          setAssetModeMessages((prevState: any[]) => {
            const assetModeData = parsedData as any;
            const filtered = prevState.filter(
              (item: any) => item?.data?.assetId !== assetModeData?.data?.assetId
            );
            return [...filtered, assetModeData];
          });
        }
      } catch (error) {
        console.error("WebSocket couldn't parse message", error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error', error);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, user, siteId, isSiteDashboardPage]);

  const contextValue = useMemo(
    () => ({
      messages,
      alarmMessage,
      assetStateMessages,
      assetModeMessages,
      reportMessage,
      reportNotificationIds,
      addReportNotificationId,
    }),
    [
      messages,
      alarmMessage,
      reportMessage,
      reportNotificationIds,
      assetStateMessages,
      assetModeMessages,
    ]
  );

  return <WebSocketsContext.Provider value={contextValue}>{children}</WebSocketsContext.Provider>;
};
